import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { type HUImage } from "@modules/types/imageTypes"
import { cva } from "class-variance-authority"
import { Dot, UserRoundIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"

export const UserCellVariants = cva("", {
  variants: {
    borderColor: {
      default: "",
      green: "ring-3 ring-custom-green-500",
      yellow: "ring-3 ring-custom-yellow-500",
      red: "ring-3 ring-red-500",
      white: "ring-3 ring-white",
      black: "ring-3 ring-black",
      neutral: "ring-3 ring-neutral-500",
    },
    subtitleColor: {
      default: "",
      green: "text-custom-green-500",
      yellow: "text-custom-yellow-500",
      red: "text-red-500",
      white: "text-white",
      black: "text-black",
      neutral: "text-neutral-500",
    },
  },
  defaultVariants: {
    borderColor: "default",
    subtitleColor: "default",
  },
})

export type UserCellVariants = "default" | "green" | "yellow" | "red" | "white" | "black" | "neutral" | null | undefined

type UserCellSkeletonProps = {
  subtitle?: boolean
  avatarSize?: string
  className?: string
} & React.ComponentProps<"div">

export function UserCellSkeleton({ subtitle = false, avatarSize, className, ...props }: UserCellSkeletonProps) {
  return (
    <div className={cn("flex w-full flex-row gap-2", className)} {...props}>
      <Skeleton className={cn("rounded-full", avatarSize ?? "size-10")} />
      <div className="flex flex-col justify-center gap-1">
        <Skeleton className="h-4 w-24 rounded-sm" />
        {subtitle && <Skeleton className="h-3 w-full rounded-sm" />}
      </div>
    </div>
  )
}

type AnonymousCellProps = {
  borderColor?: UserCellVariants
  subtitle?: string | React.ReactNode
  subtitleColor?: UserCellVariants
  subtitleClassName?: string
  avatarSize?: string
  className?: string
} & React.ComponentProps<"div">

export function AnonymousCell({
  borderColor,
  subtitle,
  subtitleColor,
  subtitleClassName,
  avatarSize,
  className,
  ...props
}: AnonymousCellProps) {
  return (
    <div className={cn("flex gap-2", className)} {...props}>
      <div className="relative flex w-max items-center">
        <div
          className={cn(
            "flex items-center justify-center rounded-full bg-neutral-200",
            UserCellVariants({ borderColor }),
            avatarSize ?? "size-10",
          )}
        >
          <UserRoundIcon className="text-neutral-900" size={30} />
        </div>
      </div>
      <div className="flex w-full flex-col justify-center overflow-hidden overflow-ellipsis">
        <Text fontStyle="LL" className="line-clamp-1 overflow-ellipsis">
          {word("global.text.anonymous")}
        </Text>
        {typeof subtitle === "string" ? (
          <Text
            fontStyle="LS"
            className={cn("line-clamp-1 overflow-ellipsis", UserCellVariants({ subtitleColor }), subtitleClassName)}
          >
            {subtitle}
          </Text>
        ) : (
          (subtitle ?? null)
        )}
      </div>
    </div>
  )
}

type UserCellProps = {
  member: { avatar?: HUImage | null; username?: string; firstname: string; lastname: string }
  borderColor?: UserCellVariants
  subtitle?: string | React.ReactNode
  subtitleColor?: UserCellVariants
  subtitleClassName?: string
  avatarSize?: string
  className?: string
  isOnline?: boolean
} & React.ComponentProps<"div">

export function UserCell({
  member,
  borderColor,
  subtitle,
  subtitleColor,
  subtitleClassName,
  avatarSize,
  isOnline,
  className,
  ref,
  ...props
}: UserCellProps) {
  return (
    <div ref={ref} className={cn("flex gap-2", className)} {...props}>
      <div className="relative flex w-max items-center">
        <ProfileAvatar
          className={cn(UserCellVariants({ borderColor }), avatarSize)}
          profile={{ avatar: member.avatar ?? null, username: member.username ?? member.firstname }}
        />
        {isOnline && (
          <div className="absolute" style={{ top: -7, right: -4 }}>
            <Dot className="size-3 text-textSuccess" />
          </div>
        )}
      </div>
      <div className="flex w-full flex-col justify-center overflow-hidden overflow-ellipsis">
        <Text
          fontStyle="LL"
          className="line-clamp-1 overflow-ellipsis"
          title={member.firstname + " " + member.lastname}
        >
          {member.firstname + " " + member.lastname}
        </Text>
        {typeof subtitle === "string" ? (
          <Text
            fontStyle="LS"
            className={cn("line-clamp-1 overflow-ellipsis", UserCellVariants({ subtitleColor }), subtitleClassName)}
          >
            {subtitle}
          </Text>
        ) : (
          (subtitle ?? null)
        )}
      </div>
    </div>
  )
}

type UserCellWithTooltipProps = UserCellProps & {
  tooltipContent?: React.ReactNode
  tooltipProps?: Omit<React.ComponentProps<typeof Tooltip>, "children">
  tooltipTriggerProps?: Omit<React.ComponentProps<typeof TooltipTrigger>, "children" | "asChild">
  tooltipContentProps?: Omit<React.ComponentProps<typeof TooltipContent>, "children">
}

export function UserCellWithTooltip({
  tooltipContent,
  tooltipProps,
  tooltipTriggerProps,
  tooltipContentProps,
  member,
  subtitle,
  ...props
}: UserCellWithTooltipProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const checkTruncation = () => {
      if (containerRef.current) {
        const textElements = containerRef.current.querySelectorAll("[class*='truncate'], [class*='line-clamp']")
        for (const el of textElements) {
          if (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight) {
            setIsTruncated(true)
            return
          }
        }
        setIsTruncated(false)
      }
    }

    checkTruncation()
    window.addEventListener("resize", checkTruncation)
    return () => window.removeEventListener("resize", checkTruncation)
  }, [member.firstname, member.lastname, member.username, subtitle])

  const defaultTooltipContent = (
    <div className="flex flex-col gap-1">
      <Text fontStyle="LM" className="wrap-break-word">
        {member.firstname + " " + member.lastname}
      </Text>
      {member.username && (
        <Text fontStyle="LS" className="wrap-break-word text-neutral-400">
          @{member.username}
        </Text>
      )}
    </div>
  )

  const content = <UserCell ref={containerRef} member={member} subtitle={subtitle} {...props} />

  if (!isTruncated) {
    return content
  }

  return (
    <Tooltip {...tooltipProps}>
      <TooltipTrigger {...tooltipTriggerProps} render={content} />
      <TooltipContent {...tooltipContentProps}>{tooltipContent ?? defaultTooltipContent}</TooltipContent>
    </Tooltip>
  )
}
